import { useEffect, useRef, useState } from "react";
import { Plus, FileText, Download, Trash2, RefreshCw, CheckCircle2, Clock, X, Pencil } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { useConfirm } from "../components/ConfirmDialog";
import { SopsSkeleton } from "../components/PageSkeletons";

export default function Sops() {
  const confirm = useConfirm();
  const [sops, setSops] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", department: "", version: "1.0", notes: "" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef();

  const [editSop, setEditSop] = useState(null);
  const [editForm, setEditForm] = useState({ title: "", version: "" });
  const [editFile, setEditFile] = useState(null);
  const [editBusy, setEditBusy] = useState(false);
  const editFileRef = useRef();

  const [viewSop, setViewSop] = useState(null);
  const [viewText, setViewText] = useState("");
  const [viewLoading, setViewLoading] = useState(false);

  const load = () => {
    api.get("/sops").then(({ data }) => setSops(data)).catch(() => setSops([]));
    api.get("/departments").then(({ data }) => setDepartments(data)).catch(() => {});
  };
  useEffect(() => {
    load();
    // Background retries land on the server periodically -- poll so a "Sync failed"
    // badge clears itself once one succeeds, no manual refresh needed.
    const interval = setInterval(load, 10000);
    return () => clearInterval(interval);
  }, []);

  const upload = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (file) fd.append("file", file);
      const { data } = await api.post("/sops", fd);
      if (data.sync_status === "synced") {
        toast.success("SOP uploaded", { description: "Synced to local server" });
      } else if (data.sync_status === "failed") {
        toast.warning("SOP uploaded", { description: "Auto-sync failed — retry from the card" });
      } else {
        toast.success("SOP uploaded");
      }
      setOpen(false);
      setForm({ title: "", department: "", version: "1.0", notes: "" });
      setFile(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const sync = async (sop) => {
    const t = toast.loading(`Syncing '${sop.title}' to local server LS-01…`);
    try {
      await api.post(`/sops/${sop.id}/sync`);
      toast.success("SOP synced to local server", { id: t });
      load();
    } catch (err) {
      toast.error(formatApiError(err), { id: t });
    }
  };

  const remove = async (sop) => {
    const ok = await confirm({
      title: `Delete SOP '${sop.title}'?`,
      description: "This cannot be undone.",
      confirmLabel: "Delete",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/sops/${sop.id}`);
      toast.success("SOP deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const download = (sop) => {
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/sops/${sop.id}/download`, "_blank");
  };

  const openEdit = (sop) => {
    setEditSop(sop);
    setEditForm({ title: sop.title, version: sop.version });
    setEditFile(null);
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    setEditBusy(true);
    try {
      const fd = new FormData();
      fd.append("title", editForm.title);
      fd.append("version", editForm.version);
      if (editFile) fd.append("file", editFile);
      const { data } = await api.patch(`/sops/${editSop.id}`, fd);
      if (data.sync_status === "synced") {
        toast.success("SOP updated", { description: "Synced to local server" });
      } else if (data.sync_status === "failed") {
        toast.warning("SOP updated", { description: "Auto-sync failed — retry from the card" });
      } else {
        toast.success("SOP updated");
      }
      setEditSop(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setEditBusy(false);
    }
  };

  const openView = async (sop) => {
    setViewSop(sop);
    setViewText("");
    setViewLoading(true);
    try {
      const { data } = await api.get(`/sops/${sop.id}/text`);
      setViewText(data.text);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setViewLoading(false);
    }
  };

  return (
    <div className="space-y-8 fade-up">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <h1 className="font-heading font-light text-3xl tracking-tight" data-testid="sops-title">SOP Library</h1>
          <p className="text-sm text-neutral-500 mt-1">One active procedure per department. Only SOPs travel to the local server — never voice.</p>
        </div>
        <button data-testid="upload-sop-button" onClick={() => setOpen(true)}
          className="inline-flex items-center justify-center gap-2 bg-[#111] text-white rounded-full px-5 py-2.5 text-sm font-medium hover:bg-neutral-800 transition-colors whitespace-nowrap self-start">
          <Plus className="w-4 h-4 shrink-0" /> Upload SOP
        </button>
      </div>

      {sops === null ? <SopsSkeleton /> : (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {sops.map((sop) => (
          <div key={sop.id} data-testid={`sop-card-${sop.id}`} onClick={() => openView(sop)}
            className="bg-white border border-neutral-200 rounded-lg p-6 cursor-pointer hover:-translate-y-0.5 hover:shadow-lg transition-[transform,box-shadow] duration-200">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-md bg-neutral-100 border border-neutral-200 flex items-center justify-center">
                  <FileText className="w-4 h-4 text-neutral-600" />
                </div>
                <div>
                  <h3 className="font-medium text-sm">{sop.title}</h3>
                  <p className="text-xs text-neutral-400 font-mono-data mt-0.5">{sop.department} · v{sop.version}</p>
                </div>
              </div>
              {sop.sync_status === "synced" ? (
                <span className="inline-flex items-center gap-1 text-[11px] text-green-700 bg-green-50 border border-green-200 rounded-full px-2.5 py-1" data-testid={`sop-status-${sop.id}`}>
                  <CheckCircle2 className="w-3 h-3" /> Synced
                </span>
              ) : sop.sync_status === "failed" ? (
                <span className="inline-flex items-center gap-1 text-[11px] text-red-700 bg-red-50 border border-red-200 rounded-full px-2.5 py-1" data-testid={`sop-status-${sop.id}`}>
                  <Clock className="w-3 h-3" /> Sync failed
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-1" data-testid={`sop-status-${sop.id}`}>
                  <Clock className="w-3 h-3" /> Pending sync
                </span>
              )}
            </div>
            {sop.notes && <p className="text-xs text-neutral-500 mt-4 leading-relaxed">{sop.notes}</p>}
            <div className="flex items-center justify-between mt-5 pt-4 border-t border-neutral-100">
              <p className="text-[11px] text-neutral-400 font-mono-data">
                {sop.file_name ? `${sop.file_name} · ${(sop.file_size / 1024).toFixed(0)} KB` : "Metadata only"}
              </p>
              <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                {sop.sync_status === "failed" && (
                  <button data-testid={`sync-sop-${sop.id}`} onClick={() => sync(sop)} title="Sync to local server"
                    className="p-2 rounded-md text-neutral-500 hover:text-[#111] hover:bg-neutral-100 transition-colors">
                    <RefreshCw className="w-4 h-4" />
                  </button>
                )}
                {sop.file_name && (
                  <button data-testid={`download-sop-${sop.id}`} onClick={() => download(sop)} title="Download"
                    className="p-2 rounded-md text-neutral-500 hover:text-[#111] hover:bg-neutral-100 transition-colors">
                    <Download className="w-4 h-4" />
                  </button>
                )}
                <button data-testid={`edit-sop-${sop.id}`} onClick={() => openEdit(sop)} title="Edit"
                  className="p-2 rounded-md text-neutral-500 hover:text-[#111] hover:bg-neutral-100 transition-colors">
                  <Pencil className="w-4 h-4" />
                </button>
                <button data-testid={`delete-sop-${sop.id}`} onClick={() => remove(sop)} title="Delete"
                  className="p-2 rounded-md text-neutral-400 hover:text-red-600 hover:bg-red-50 transition-colors">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
        {sops.length === 0 && (
          <div className="md:col-span-2 bg-white border border-neutral-200 rounded-lg p-12 text-center text-sm text-neutral-400">
            No SOPs uploaded yet.
          </div>
        )}
      </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md" data-testid="upload-sop-modal">
          <DialogHeader>
            <DialogTitle className="font-heading font-medium">Upload SOP</DialogTitle>
            <DialogDescription>PDF or DOCX. Replaces the existing SOP for that department.</DialogDescription>
          </DialogHeader>
          <form onSubmit={upload} className="space-y-4 mt-2 min-w-0">
            <div>
              <label className="text-xs font-medium text-neutral-600 uppercase tracking-wide">Title</label>
              <input data-testid="sop-title-input" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                className="mt-1.5 w-full border border-neutral-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#111]" placeholder="Guest Welcome Protocol" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-neutral-600 uppercase tracking-wide">Department</label>
                <Select value={form.department} onValueChange={(v) => setForm({ ...form, department: v })} required>
                  <SelectTrigger data-testid="sop-department-select" className="mt-1.5">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    {departments.map((d) => <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium text-neutral-600 uppercase tracking-wide">Version</label>
                <input data-testid="sop-version-input" value={form.version} onChange={(e) => setForm({ ...form, version: e.target.value })}
                  className="mt-1.5 w-full border border-neutral-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#111]" />
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-neutral-600 uppercase tracking-wide">Document (PDF / DOCX / TXT)</label>
              <div className="mt-1.5 h-11 flex items-center gap-3 border border-neutral-300 rounded-md px-3.5">
                {file ? (
                  <>
                    <span className="flex-1 min-w-0 truncate text-sm text-neutral-700" data-testid="sop-selected-file-name">{file.name}</span>
                    <button type="button" data-testid="sop-remove-file-button"
                      onClick={() => { setFile(null); if (fileRef.current) fileRef.current.value = ""; }}
                      title="Remove file" className="p-1 rounded-md text-neutral-400 hover:text-red-600 hover:bg-red-50 transition-colors shrink-0">
                      <X className="w-4 h-4" />
                    </button>
                  </>
                ) : (
                  <input data-testid="sop-file-input" ref={fileRef} type="file" accept=".pdf,.docx,.doc,.txt"
                    onChange={(e) => setFile(e.target.files[0] || null)}
                    className="w-full min-w-0 text-sm text-neutral-500 file:mr-3 file:rounded-full file:border-0 file:bg-neutral-100 file:px-3.5 file:py-1.5 file:text-xs file:font-medium hover:file:bg-neutral-200" />
                )}
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-neutral-600 uppercase tracking-wide">Notes (optional)</label>
              <textarea data-testid="sop-notes-input" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="mt-1.5 w-full border border-neutral-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#111]" placeholder="What changed in this version…" />
            </div>
            <button data-testid="sop-submit-button" type="submit" disabled={busy || !form.department}
              className="w-full bg-[#111] text-white rounded-full py-3 text-sm font-medium hover:bg-neutral-800 transition-colors disabled:opacity-50">
              {busy ? "Uploading…" : "Upload SOP"}
            </button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editSop} onOpenChange={(v) => !v && setEditSop(null)}>
        <DialogContent className="sm:max-w-md" data-testid="edit-sop-modal">
          <DialogHeader>
            <DialogTitle className="font-heading font-medium">Edit SOP</DialogTitle>
            <DialogDescription>Uploading a new document replaces the current SOP text and re-syncs it.</DialogDescription>
          </DialogHeader>
          <form onSubmit={saveEdit} className="space-y-4 mt-2 min-w-0">
            <div>
              <label className="text-xs font-medium text-neutral-600 uppercase tracking-wide">Title</label>
              <input data-testid="edit-sop-title-input" required value={editForm.title}
                onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                className="mt-1.5 w-full border border-neutral-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#111]" />
            </div>
            <div>
              <label className="text-xs font-medium text-neutral-600 uppercase tracking-wide">Version</label>
              <input data-testid="edit-sop-version-input" value={editForm.version}
                onChange={(e) => setEditForm({ ...editForm, version: e.target.value })}
                className="mt-1.5 w-full border border-neutral-300 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#111]" />
            </div>
            <div>
              <label className="text-xs font-medium text-neutral-600 uppercase tracking-wide">Replace document (optional)</label>
              <div className="mt-1.5 h-11 flex items-center gap-3 border border-neutral-300 rounded-md px-3.5">
                {editFile ? (
                  <>
                    <span className="flex-1 min-w-0 truncate text-sm text-neutral-700" data-testid="edit-sop-selected-file-name">{editFile.name}</span>
                    <button type="button" data-testid="edit-sop-remove-file-button"
                      onClick={() => { setEditFile(null); if (editFileRef.current) editFileRef.current.value = ""; }}
                      title="Remove file" className="p-1 rounded-md text-neutral-400 hover:text-red-600 hover:bg-red-50 transition-colors shrink-0">
                      <X className="w-4 h-4" />
                    </button>
                  </>
                ) : (
                  <input data-testid="edit-sop-file-input" ref={editFileRef} type="file" accept=".pdf,.docx,.doc,.txt"
                    onChange={(e) => setEditFile(e.target.files[0] || null)}
                    className="w-full min-w-0 text-sm text-neutral-500 file:mr-3 file:rounded-full file:border-0 file:bg-neutral-100 file:px-3.5 file:py-1.5 file:text-xs file:font-medium hover:file:bg-neutral-200" />
                )}
              </div>
            </div>
            <button data-testid="edit-sop-submit-button" type="submit" disabled={editBusy}
              className="w-full bg-[#111] text-white rounded-full py-3 text-sm font-medium hover:bg-neutral-800 transition-colors disabled:opacity-50">
              {editBusy ? "Saving…" : "Save changes"}
            </button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!viewSop} onOpenChange={(v) => !v && setViewSop(null)}>
        <DialogContent className="sm:max-w-lg" data-testid="view-sop-modal">
          <DialogHeader>
            <DialogTitle className="font-heading font-medium">{viewSop?.title}</DialogTitle>
            <DialogDescription>{viewSop?.department} · v{viewSop?.version}</DialogDescription>
          </DialogHeader>
          <div data-testid="view-sop-text" className="mt-2 max-h-[60vh] overflow-y-auto whitespace-pre-wrap text-sm text-neutral-700 leading-relaxed border border-neutral-200 rounded-md p-4 bg-neutral-50">
            {viewLoading ? "Loading…" : (viewText || "No text available for this SOP.")}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
